<template>
  <SFFilterGroup
    v-if="!hideSorting"
    :label="$t('filter_slide_in_content.sorting')"
    class="md:hidden"
  >
    <SFSortSelection :category="category" />
  </SFFilterGroup>

  <template v-for="(filter, index) in availableFilters" :key="filter.slug">
    <template
      v-if="
        filter.type === 'range' &&
        filter.values[0]?.min !== filter.values[0]?.max
      "
    >
      <SFFilterGroup
        v-if="filter.slug === 'max_savings_percentage'"
        :class="{ 'border-t': index !== 0 }"
        :label="filter.name"
        :show-action="
          !!(appliedFilter.maxReduction || appliedFilter.minReduction) &&
          (appliedFilter.minReduction !== filter.values[0]?.min ||
            appliedFilter.maxReduction !== filter.values[0]?.max)
        "
        @click-reset="$emit('resetReductionFilter')"
      >
        <SfReductionSlider
          :max="filter.values[0]?.max"
          :min="filter.values[0]?.min"
          :model-value="
            getReductionRange(filter.values[0].min, filter.values[0].max)
          "
          class="mt-10"
          filter-slug="max_savings_percentage"
          @drag-end="
            handleApplyOrResetReductionFilterEmit(
              $event,
              filter.values[0].min,
              filter.values[0].max,
            )
          "
          @input-field-update="
            handleApplyOrResetReductionFilterEmit(
              $event,
              filter.values[0].min,
              filter.values[0].max,
            )
          "
        />
      </SFFilterGroup>
      <SFFilterGroup
        v-else-if="filter.slug === 'prices'"
        :class="{ 'border-t': index !== 0 }"
        :label="filter.name"
        :show-action="
          !!(appliedFilter.maxPrice || appliedFilter.minPrice) &&
          (appliedFilter.minPrice !== filter.values[0]?.min ||
            appliedFilter.maxPrice !== filter.values[0]?.max)
        "
        @click-reset="$emit('resetPriceFilter')"
      >
        <SFPriceRangeSlider
          :max="filter.values[0]?.max"
          :min="filter.values[0]?.min"
          :model-value="
            getPriceRange(filter.values[0].min, filter.values[0].max)
          "
          class="mt-10"
          @drag-end="
            handleApplyOrResetPriceFilterEmit(
              $event,
              filter.values[0].min,
              filter.values[0].max,
            )
          "
          @input-field-update="
            handleApplyOrResetPriceFilterEmit(
              $event,
              filter.values[0].min,
              filter.values[0].max,
            )
          "
        />
      </SFFilterGroup>
    </template>

    <SFFilterGroup
      v-if="filter.type === 'boolean'"
      :class="{ 'border-t': index !== 0 }"
    >
      <SFSwitch
        :id="filter.slug"
        :label="getBooleanFilterLabel(filter)"
        :model-value="appliedBooleanValues[filter.slug]"
        @update:model-value="$emit('applyBooleanFilter', filter.slug, $event)"
      />
    </SFFilterGroup>
    <template v-if="filter.type === 'attributes'">
      <SFFilterGroup
        v-if="filter.slug === 'color'"
        :badge="appliedAttributeValues[filter.slug]?.length"
        :class="{ 'border-t': index !== 0 }"
        :label="filter.name"
        class="pb-9 xl:pb-9"
        @click-reset="$emit('reset', filter.slug)"
      >
        <div class="flex flex-wrap gap-y-4">
          <template v-for="item in filter.values" :key="item.id">
            <SFFilterColorChip
              v-if="ProductColor[item.name]"
              :color="item"
              :is-checked="
                appliedAttributeValues[filter.slug]?.includes(item.id)
              "
              data-testid="filter-color-circle"
              @change="$emit('applyAttributeFilter', filter.slug, item.id)"
            >
              {{ item.name }}
            </SFFilterColorChip>
          </template>
        </div>
      </SFFilterGroup>
      <SFFilterGroup
        v-else-if="filter.slug === 'size'"
        :badge="appliedAttributeValues[filter.slug]?.length"
        :class="{ 'border-t': index !== 0 }"
        :label="filter.name"
        @click-reset="$emit('reset', filter.slug)"
      >
        <div class="flex flex-wrap items-start gap-4">
          <SFChip
            v-for="item in sortedSizes"
            :key="item.id"
            :input-aria-label="
              $t('filter_slide_in_content.size_option', {
                size: formatShoeSize(item.name),
              })
            "
            :item="item.id"
            :label="formatShoeSize(item.name)"
            :model-value="appliedAttributeValues[filter.slug]"
            @change="$emit('applyAttributeFilter', filter.slug, item.id)"
          >
            {{ formatShoeSize(item.name) }}
          </SFChip>
        </div>
      </SFFilterGroup>
      <SFFilterGroup
        v-if="filter.slug === 'brand'"
        :badge="appliedAttributeValues[filter.slug]?.length"
        :class="{ 'border-t': index !== 0 }"
        :label="filter.name"
        @click-reset="$emit('reset', filter.slug)"
      >
        <div class="flex flex-col gap-2">
          <SFCheckbox
            v-for="item in filterBrands(filter.values)"
            :id="item.name"
            :key="item.id"
            :item="item.id"
            :label="item.name"
            :model-value="appliedAttributeValues[filter.slug]"
            @change="$emit('applyAttributeFilter', filter.slug, item.id)"
          />
        </div>
      </SFFilterGroup>
      <SFFilterGroup
        v-else-if="
          filter.slug !== 'size' &&
          filter.slug !== 'color' &&
          filter.slug !== 'brand'
        "
        :badge="appliedAttributeValues[filter.slug]?.length"
        :class="{ 'border-t': index !== 0 }"
        :label="
          filter.slug !== 'onlineExclusiveFilter'
            ? filter.name
            : $t('badge_labels.online_exclusive')
        "
        @click-reset="$emit('reset', filter.slug)"
      >
        <div class="flex flex-col gap-2">
          <SFCheckbox
            v-for="item in filter.values"
            :id="item.name"
            :key="item.id"
            :item="item.id"
            :label="item.name"
            @change="$emit('applyAttributeFilter', filter.slug, item.id)"
          />
        </div>
      </SFFilterGroup>
    </template>
  </template>
</template>

<script lang="ts" setup>
import type {
  BooleanFilterItemWithValues,
  Category,
  CentAmount,
  FilterItemWithValues,
  ProductSearchQuery,
} from '@scayle/storefront-nuxt'
import type { RangeTuple } from '@scayle/storefront-product-listing'
import { computed } from 'vue'
import SFSortSelection from '../sorting/SFSortSelection.vue'
import SFFilterColorChip from './SFFilterColorChip.vue'
import SFFilterGroup from './SFFilterGroup.vue'
import { ProductColor } from '~/constants/product'
import {
  SFCheckbox,
  SFChip,
  SFPriceRangeSlider,
  SFSwitch,
} from '#storefront-ui/components'
import { useI18n } from '#i18n'
import { useFormat } from '~/composables/useFormat'
import { type FilterValue, useSizeSorting } from '~/composables/useSizeSorting'
import SfReductionSlider from '#storefront-ui/components/form/SFReductionSlider.vue'

const { appliedFilter, availableFilters, category } = defineProps<{
  category: Category
  availableFilters?: FilterItemWithValues[]
  appliedAttributeValues: Record<string, (string | number)[]>
  appliedBooleanValues: Record<string, boolean>
  appliedFilter: ProductSearchQuery
  hideSorting: boolean
}>()

const emit = defineEmits<{
  resetPriceFilter: []
  resetReductionFilter: []
  applyReductionFilter: [range: RangeTuple]
  applyPriceFilter: [range: RangeTuple]
  applyBooleanFilter: [slug: string, value: boolean]
  reset: [slug: string]
  applyAttributeFilter: [slug: string, value: number]
}>()

const { t: $t } = useI18n()
const { formatShoeSize } = useFormat()

const { sortSizeValues } = useSizeSorting()
const sortedSizes = computed(() => {
  const sizeFilter = availableFilters?.find((f) => f.slug === 'size')
  return sizeFilter ? sortSizeValues(sizeFilter.values as FilterValue[]) : []
})

/**
 * Emits an event based on whether the provided event values match the filter limits.
 * This ensures that the price filter can be reset even when the right / left slider
 * is at its highest position, addressing the issue where the filter would
 * otherwise remain applied.
 */
const handleApplyOrResetPriceFilterEmit = (
  event: RangeTuple,
  filterMin: number,
  filterMax: number,
) => {
  if (event[0] === filterMin && event[1] === filterMax) {
    emit('resetPriceFilter')
  } else {
    emit('applyPriceFilter', event)
  }
}
const handleApplyOrResetReductionFilterEmit = (
  event: RangeTuple,
  filterMin: number,
  filterMax: number,
) => {
  if (event[0] === filterMin && event[1] === filterMax) {
    emit('resetReductionFilter')
  } else {
    emit('applyReductionFilter', event)
  }
}

const getPriceRange = (
  currentMin: CentAmount,
  currentMax: CentAmount,
): RangeTuple => {
  const appliedMin = appliedFilter.minPrice
  const appliedMax = appliedFilter.maxPrice

  const min = Math.max(currentMin, appliedMin ?? currentMin, 0)
  const max = Math.min(currentMax, appliedMax ?? currentMax)

  return [min, max]
}

const getReductionRange = (
  currentMin: number,
  currentMax: number,
): RangeTuple => {
  const appliedMin = appliedFilter.minReduction
  const appliedMax = appliedFilter.maxReduction

  const min = Math.max(currentMin, appliedMin ?? currentMin, 0)
  const max = Math.min(currentMax, appliedMax ?? currentMax)

  return [min, max]
}

const getBooleanFilterLabel = ({ name, slug }: BooleanFilterItemWithValues) => {
  return slug !== 'sale' ? name : $t('filter_slide_in_content.only_sale_option')
}

const filterBrands = (
  brands: {
    name: string
    id: number
    productCount: number
    value: string
  }[],
) => {
  return [...brands].sort((a, b) =>
    a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase()),
  )
}
</script>
